/**
 * Console: comités adicionales de un usuario (personal de la oficina que
 * opera varios comités). El usuario sigue siendo de su comité; cada acceso
 * le abre otro con un rol de ese comité y aparece en su selector de comité.
 * Quitar un acceso corta de inmediato su sesión en ese comité.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Trash2 } from "lucide-react";

import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Button, Cargando } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  listarAccesosUsuario,
  listarEmpresas,
  listarRolesEmpresa,
  otorgarAccesoUsuario,
  revocarAccesoUsuario,
  type UsuarioRead,
} from "./api";

const selectCls =
  "block w-full rounded-md border border-neutral-450 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

export function AccesosComiteModal({
  empresaId,
  usuario,
  onClose,
}: {
  empresaId: number;
  usuario: UsuarioRead;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const accesosKey = ["superadmin", "accesos", usuario.id];

  const accesosQ = useQuery({
    queryKey: accesosKey,
    queryFn: () => listarAccesosUsuario(empresaId, usuario.id),
  });
  const empresasQ = useQuery({ queryKey: ["superadmin", "empresas"], queryFn: listarEmpresas });

  const [destino, setDestino] = useState<number | "">("");
  const [role, setRole] = useState("");
  const rolesQ = useQuery({
    queryKey: ["superadmin", "empresa", destino, "roles"],
    queryFn: () => listarRolesEmpresa(destino as number),
    enabled: destino !== "",
  });

  const yaConAcceso = new Set((accesosQ.data ?? []).map((a) => a.empresa_id));
  const candidatos = (empresasQ.data ?? [])
    .map((e) => e.empresa)
    .filter((e) => e.is_active && e.id !== empresaId && !yaConAcceso.has(e.id))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

  const refrescar = (): void => {
    void queryClient.invalidateQueries({ queryKey: accesosKey });
  };
  const errorMsg = (err: unknown, fallback: string): string =>
    err instanceof ApiError ? err.message : fallback;

  const otorgarMut = useMutation({
    mutationFn: () =>
      otorgarAccesoUsuario(empresaId, usuario.id, { empresa_id: destino as number, role }),
    onSuccess: () => {
      toast.success("Acceso otorgado");
      setDestino("");
      setRole("");
      refrescar();
    },
    onError: (err) => toast.error(errorMsg(err, "No se pudo otorgar el acceso")),
  });

  const revocarMut = useMutation({
    mutationFn: (empresaDestinoId: number) =>
      revocarAccesoUsuario(empresaId, usuario.id, empresaDestinoId),
    onSuccess: () => {
      toast.success("Acceso quitado");
      refrescar();
    },
    onError: (err) => toast.error(errorMsg(err, "No se pudo quitar el acceso")),
  });

  const accesos = accesosQ.data ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title="Comités adicionales"
      subtitle={`${usuario.nombre} · ${usuario.email}`}
      icon={Building2}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <div className="space-y-5">
        <p className="text-sm text-neutral-600">
          Para personal de la oficina que opera varios comités. Cada acceso aparece en su
          selector de comité, con el rol que elijas para ese comité.
        </p>

        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Accesos actuales
          </h3>
          {accesosQ.isLoading ? (
            <Cargando inline />
          ) : accesos.length === 0 ? (
            <p className="text-sm text-neutral-500">Sólo tiene acceso a su propio comité.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200">
              {accesos.map((a) => (
                <li key={a.empresa_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="flex-1 font-medium text-neutral-900">{a.empresa_nombre}</span>
                  <span className="capitalize text-neutral-500">{a.role}</span>
                  <button
                    type="button"
                    onClick={() => revocarMut.mutate(a.empresa_id)}
                    disabled={revocarMut.isPending}
                    className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-md border border-neutral-200 p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Agregar comité
          </h3>
          <div className="grid gap-2 sm:grid-cols-[1fr_180px_auto] sm:items-end">
            <label className="text-sm">
              <span className="mb-1 block text-neutral-600">Comité</span>
              <select
                value={destino}
                onChange={(e) => {
                  setDestino(e.target.value === "" ? "" : Number(e.target.value));
                  setRole("");
                }}
                className={selectCls}
              >
                <option value="">Elegir…</option>
                {candidatos.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-neutral-600">Rol en ese comité</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={destino === ""}
                className={selectCls}
              >
                <option value="">Elegir…</option>
                {(rolesQ.data ?? [])
                  .filter((r) => r.is_active)
                  .map((r) => (
                    <option key={r.id} value={r.code}>
                      {r.nombre}
                    </option>
                  ))}
              </select>
            </label>
            <Button
              icon={Plus}
              onClick={() => otorgarMut.mutate()}
              disabled={destino === "" || role === "" || otorgarMut.isPending}
            >
              Agregar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
